import { ExhibitionCollectionFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { HeadlineText, BodyText, CalendarPlusIcon } from 'design-configuration/components'
import { ExhibitionImageCurl } from '../../[slug]/components/exhibition-image-curl'
import { WorksDigitalReleaseCard } from '../../[slug]/components/works-digital-card'
import { WorksPhysicalReleaseCard } from '../../[slug]/components/works-physical-card'
import { zachAnchor, zachShopifyProduct, zachTitle } from '../display'
import { WorksQuote } from '../../[slug]/components/works'
import { articleRoute } from '@utils/routes'
import { AddToCalendarButton, getProjectEvent, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { ValidStatusType } from '@utils/project'
import { useProjectStatus } from '@/hooks/use-project-status'
import { GROUP_EXHIBITION_Q4_2024_SLUG } from '@consts/common'
import { getPhysicalProjectEvent } from '../../[slug]/utils/exhibition-utils'

export const ZachWork = ({ collection }: { collection?: ExhibitionCollectionFragment }): React.ReactElement | null => {
  const digitalProject = collection?.Collection?.Project ?? undefined
  const physicalProject = collection?.AdditionalProjects?.[0]?.Project ?? undefined
  const { status: digitalProjectStatus } = useProjectStatus(digitalProject)
  const { status: physicalProjectStatus } = useProjectStatus(physicalProject)

  if (!digitalProject || !physicalProject) {
    return null
  }

  const digitalEvent = getProjectEvent(digitalProject)
  const physicalEvent = getPhysicalProjectEvent(physicalProject, GROUP_EXHIBITION_Q4_2024_SLUG)

  return (
    <div className="gap-y-2xl flex flex-col md:gap-y-[80px]">
      <ExhibitionImageCurl
        anchor={zachAnchor}
        align={'right'}
        image1AspectRatio="aspect-[4/5]"
        image1={getPrefixedLinkPath('/images/poetics/zachCollection/zachCollection-1.jpg')}
        image2={getPrefixedLinkPath('/images/poetics/zachCollection/zachCollection-2.jpg')}
        image3={getPrefixedLinkPath('/images/poetics/zachCollection/zachCollection-3.jpg')}
        content={
          <>
            <div className="flex items-center justify-between">
              <div>
                <HeadlineText className="font-medium">{zachTitle}</HeadlineText>
                <HeadlineText className="font-light">Zach Lieberman</HeadlineText>
              </div>
              {digitalProjectStatus === ValidStatusType.upcoming ||
              physicalProjectStatus === ValidStatusType.upcoming ? (
                <AddToCalendarButton
                  events={[physicalEvent, digitalEvent]}
                  buttonName={TrackedAddToCalButtonNames.exhibitionWorksDescription}
                  colorTheme={'outline'}
                  className="bg-transparent"
                >
                  <div className="gap-x-xs flex items-center">
                    <CalendarPlusIcon className="fill-on-surface h-sm w-sm" />
                    <BodyText>Add to calendar</BodyText>
                  </div>
                </AddToCalendarButton>
              ) : null}
            </div>
            <BodyText>
              Zach Lieberman’s <span className="italic">Box Light Studies</span> invites an inquiry into the fluid
              nature of light and perception. Lieberman’s <span className="italic">Box Light Studies (sculpture)</span>
              explores light materialized in a wall-mounted sculpture that challenges viewers to engage with the
              relativity inherent in the visible spectrum. Emphasizing the fleeting yet impactful moments revealed to
              the eye through illumination, this work invites the viewer to get lost in the cascading effects of color
              shifts and refraction planes. Expanded further with the digital-only release of{' '}
              <span className="italic">Box Light Studies</span>, the collection showcases Lieberman’s distinct
              algorithmic gestures, where light refracts, flows, shifts, and morphs, casting echoes into unknown realms.
              gestures, where light refracts, flows, shifts, and morphs, casting echoes into unknown realms. Through
              this journey, Lieberman illuminates the bounded experience of seeing while confronting the desire to reach
              beyond it.
            </BodyText>
          </>
        }
      />
      <div className="gap-md px-md lg:gap-2xl lg:px-2xl flex w-full flex-col justify-center md:flex-row">
        <WorksPhysicalReleaseCard
          project={physicalProject}
          exhibitionSlug={GROUP_EXHIBITION_Q4_2024_SLUG}
          title={'Box Light Studies (sculpture)'}
          image={getPrefixedLinkPath('/images/poetics/zachCollection/zachCollection-4.jpg')}
          moreDetailsBlocks={[
            {
              title: 'Edition details',
              description: (
                <BodyText>
                  Produced from an artist-selected hash code, this sculpture embodies the expansive vision of{' '}
                  <span className="italic">Box Light Studies</span>. The sculpture is paired with a digital artwork
                  chosen by the artist.
                </BodyText>
              )
            },
            {
              title: 'Product specifications',
              description: (
                <BodyText>
                  The sculpture measures 48 x 48 x 0.5 inches and is crafted from a combination of acrylic and paper. It
                  includes a hanging armature on the verso with a wall mountable French cleat.
                </BodyText>
              )
            },
            {
              title: 'Sales notes',
              description: (
                <BodyText>
                  Available for purchase with credit card, Shop Pay, Apple Pay, Google Pay, PayPal, or Ethereum.
                  Purchase includes the physical sculpture and corresponding digital artwork, which can be claimed upon
                  purchase.
                </BodyText>
              )
            },
            {
              title: 'Shipping & returns',
              description: (
                <BodyText>
                  Please allow 2 weeks for shipping after purchase. Shipping is only available in North America (U.S.,
                  Canada, & Mexico) and the European Union and the U.K. Shipping and handling is complimentary with all
                  orders, however duties and taxes may still apply at your destination. No returns.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">Unique sculpture with digital artwork</BodyText>
              <BodyText>Fixed sale of $8,000 USD</BodyText>
            </div>
          }
          shopifyProduct={zachShopifyProduct}
        />
        <WorksDigitalReleaseCard
          project={digitalProject}
          title={digitalProject.name ?? ''}
          moreDetailsBlocks={[
            {
              title: 'Release details',
              description: (
                <div className="gap-y-xs flex flex-col">
                  <BodyText>
                    In a ranked auction with settlement, bids are placed by collectors and ranked from highest to
                    lowest. Any outbid funds are returned immediately. At the conclusion of the auction, winners pay the
                    lowest winning bid price, and any excess funds are returned upon minting the artwork.
                  </BodyText>
                  <BodyText className="text-on-surface-variant">
                    Time extensions apply: the auction will extend by up to 5 minutes if a bid is placed in the final 5
                    minutes, with a maximum total extension of 60 minutes.
                  </BodyText>
                  <AllPurposeLink
                    href={articleRoute('art-blocks-how-to-purchase')}
                    className="text-on-surface-variant gap-x-2xs flex items-center underline"
                  >
                    <BodyText>Learn more</BodyText>
                  </AllPurposeLink>
                </div>
              )
            },
            {
              title: 'Sales Notes',
              description: (
                <BodyText>
                  All bids must be placed in ETH. Winners will receive their digital artwork directly in their wallets
                  after the auction.
                </BodyText>
              )
            }
          ]}
          description={
            <div className="my-sm">
              <BodyText className="font-medium">100 unique digital artworks</BodyText>
              <BodyText>Ranked auction with settlement with a starting bid of 0.05 Ξ</BodyText>
            </div>
          }
        />
      </div>
      <WorksQuote
        quote={
          <>
            {`“When art hits, really hits, I can feel it in my body. It changes my heart rate, it tells me something about the world. And that is what I'm trying to convey through this work, through all my work; how something that seems simple is actually completely bottomless.”`}
          </>
        }
        author="Zach Lieberman"
        image={getPrefixedLinkPath('/images/poetics/zachCollection/zachCollection-5.jpg')}
        alignText="left"
      />
    </div>
  )
}
